// Address combobox (ARIA 1.2 pattern): type, pick with arrows/Enter or click, then resolve the parcel under the point.
import { useEffect, useRef, useState } from 'preact/hooks';
import { loadAddresses, searchAddresses, type Address } from '../data/address';
import { t } from '../i18n';

export function AddressSearch({ onParcel, hint }: { onParcel: (ko: number, st: string) => void; hint: string }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Address[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const all = useRef<Awaited<ReturnType<typeof loadAddresses>> | null>(null);

  useEffect(() => {
    let live = true;
    const run = async () => {
      if (!all.current) all.current = await loadAddresses().catch(() => []);
      if (!live) return;
      const h = searchAddresses(all.current, q);
      setHits(h); setActive(h.length ? 0 : -1);
      setMsg(q.trim().length >= 3 && !h.length ? t.landing.addressNone : null);
    };
    const id = setTimeout(run, 80);
    return () => { live = false; clearTimeout(id); };
  }, [q]);

  const choose = async (a: Address) => {
    setQ(a.label); setOpen(false); setBusy(true); setMsg(t.landing.addressLoading);
    try {
      const { parcelAt } = await import('../data/cadastre');
      const p = await parcelAt(a.region, a.E, a.N);
      if (!p) { setMsg(t.landing.addressNoParcel); return; }
      setMsg(null);
      onParcel(p.ko, p.st);
    } catch { setMsg(t.err.fetch); } finally { setBusy(false); }
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(hits.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (hits[active]) choose(hits[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const show = open && hits.length > 0;
  return (
    <div class="addr">
      <label for="addr-input">{t.landing.address}</label>
      <div class="combo">
        <input
          id="addr-input" type="text" role="combobox" autoComplete="off" value={q} placeholder={t.landing.addressPlaceholder}
          aria-expanded={show} aria-controls="addr-list" aria-autocomplete="list" disabled={busy}
          aria-activedescendant={show && active >= 0 ? `addr-opt-${active}` : undefined}
          onInput={(e) => { setQ((e.target as HTMLInputElement).value); setOpen(true); }}
          onFocus={() => { setOpen(true); loadAddresses().catch(() => undefined); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKey as any}
        />
        {show && (
          <ul id="addr-list" role="listbox" class="combo-list">
            {hits.map((h, i) => (
              <li key={h.label + i} id={`addr-opt-${i}`} role="option" aria-selected={i === active}
                onMouseDown={(e) => { e.preventDefault(); choose(h); }} onMouseEnter={() => setActive(i)}>{h.label}</li>
            ))}
          </ul>
        )}
      </div>
      {/* status messages replace the hint in the same slot, so the card doesn't grow while searching */}
      <p class={msg ? 'cap find-hint msg' : 'cap find-hint'} role="status">{msg ?? hint}</p>
    </div>
  );
}
